import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import FilterSet from 'components/Explorer/models/filter_set';
import useFilterLookup from 'hooks/useFilterLookup';

import AutocompleteField from '../AutocompleteField';
import HelpLink from './HelpLink';

// Maps the legacy queryMethod prop to the JSON endpoint path. Once consumers
// pass paths directly we can drop this.
const QUERY_METHOD_PATHS = {
  publisherNames: 'filters/publisher_names',
  authorNames: 'filters/author_names',
  departmentNames: 'filters/department_names',
  affiliations: 'filters/affiliations',
  funders: 'filters/funders',
  grants: 'filters/grants',
  sustainable_development_goals: 'filters/sustainable_development_goals'
};

const InputField = ({
  filterSet,
  name,
  id,
  type,
  initialSearch,
  label,
  helpUrl,
  helpTip,
  analyticsEventPrefix,
  analyticsFieldName,
  loadingMessage,
  placeholder,
  queryMethod
}) => {
  const currentValues = filterSet.get(`${name}[]`);
  const valuesKey = JSON.stringify(currentValues);
  const initialIds = useMemo(() => currentValues.map((value) => ({ id: value })), [valuesKey]);
  const selectionCount = currentValues.length;
  const fieldRef = useRef(null);

  // An unknown queryMethod produces an undefined path — gate the lookup on the
  // path itself so a misconfigured prop doesn't fire `/json_data/undefined…`.
  const path = QUERY_METHOD_PATHS[queryMethod] || '';
  const { data, loading } = useFilterLookup(path, { enabled: Boolean(path && selectionCount) });

  const items = path ? (data ?? []) : initialIds;
  useEffect(() => {
    if (!fieldRef.current) return;
    if (!selectionCount) fieldRef.current.clearAll();
  }, [selectionCount]);

  const updateSelectedItems = (selected) => {
    filterSet.set(
      `${name}[]`,
      selected.map((item) => item.id)
    );
  };

  return (
    <div className="Explorer-AdvancedSearch-field">
      <label>
        {label}
        <HelpLink href={helpUrl} tip={helpTip} analyticsFieldName={analyticsFieldName} />
      </label>
      {loading ? (
        <div>{loadingMessage}</div>
      ) : (
        <AutocompleteField
          name={name}
          id={id}
          type={type}
          initialValue={initialSearch}
          placeholder={placeholder}
          onChangeItems={updateSelectedItems}
          initiallySelected={items}
          ref={fieldRef}
          analyticsEventPrefix={analyticsEventPrefix}
          analyticsFieldName={analyticsFieldName}
        />
      )}
    </div>
  );
};

InputField.propTypes = {
  filterSet: PropTypes.instanceOf(FilterSet).isRequired,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  initialSearch: PropTypes.string,
  label: PropTypes.string.isRequired,
  helpUrl: PropTypes.string,
  helpTip: PropTypes.string,
  analyticsEventPrefix: PropTypes.string,
  analyticsFieldName: PropTypes.string,
  loadingMessage: PropTypes.string,
  placeholder: PropTypes.string,
  queryMethod: PropTypes.string
};

export default InputField;
